/* eslint-disable no-console */
// AppDynamics Configuration and Initialization

import type { AppConfig } from "@pershing/shell-comms";

/**
 * Interface for AppDynamics configuration object
 */
interface AdrumConfig {
  appKey?: string;
  adrumExtUrlHttp?: string;
  adrumExtUrlHttps?: string;
  beaconUrlHttp?: string;
  beaconUrlHttps?: string;
  xd?: { enable: boolean };
  resTiming?: { bufSize: number };
  maxUrlLength?: number;
  page?: { captureTitle: boolean };
  spa?: { spa2: boolean };
  isZonePromise?: boolean;
  angular?: boolean;
  releaseId?: string;
  enableCoreWebVitals?: boolean;
  enableSpeedIndex?: boolean;
}

/**
 * Extend Window interface to include AppDynamics properties
 */
declare global {
  interface Window {
    appConfig: AppConfig;
    ASSETS_BASE_URL: string;
    "adrum-use-strict-domain-cookies"?: boolean;
    "adrum-start-time"?: number;
    "adrum-config"?: AdrumConfig;
  }
}

/**
 * Configures AppDynamics settings
 */
export function configureAppDynamics(config: AdrumConfig): void {
  const appConfig = window.appConfig;
  const assetsBaseUrl = window.ASSETS_BASE_URL;

  config.appKey = appConfig.environment === "prod" 
    ? "AD-AAB-ABX-UUA" 
    : "AD-AAB-ABX-USH";
  config.adrumExtUrlHttp = "http://cdn.appdynamics.com";
  config.adrumExtUrlHttps = "https://cdn.appdynamics.com";
  config.beaconUrlHttp = "http://pdx-col.eum-appdynamics.com";
  config.beaconUrlHttps = "https://pdx-col.eum-appdynamics.com";
  config.xd = { enable: false };
  config.resTiming = { bufSize: 200 };
  config.maxUrlLength = 512;
  config.page = { captureTitle: false };
  config.spa = { spa2: true };
  config.isZonePromise = true;
  config.angular = true;
  // Extract release ID from ASSETS_BASE_URL
  config.releaseId = assetsBaseUrl.substring(
    assetsBaseUrl.lastIndexOf("@") + 1,
    assetsBaseUrl.length
  );
  config.enableCoreWebVitals = true;
  config.enableSpeedIndex = true;
}

/**
 * Initializes AppDynamics Real User Monitoring (RUM) agent
 * Configures and loads the AppDynamics script for application monitoring
 */
export function setAppDynamics(): void {
  // Validate required configuration
  if (!window.appConfig || !window.ASSETS_BASE_URL) {
    console.error("AppDynamics setup error: ASSETS_BASE_URL or appConfig is missing");
    return;
  }

  // Set AppDynamics cookie configuration
  window["adrum-use-strict-domain-cookies"] = true;
  window["adrum-start-time"] = new Date().getTime();

  // Configure AppDynamics settings
  const config = window["adrum-config"] || (window["adrum-config"] = {});
  configureAppDynamics(config);

  // Load AppDynamics script
  loadAppDynamicsScript();
}

/**
 * Loads the AppDynamics script by appending it to the document body
 * Separated for easier testing
 */
export function loadAppDynamicsScript(): void {
  const script = document.createElement("script");
  script.src = "//cdn.appdynamics.com/adrum/adrum-latest.js";
  document.body.appendChild(script);
}

// END App Dynamics Script
